import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import cn from 'classnames';
import dibsCss from 'dibs-css';
import { usePaymentMethodSelectContext } from './PaymentMethodSelectContext';
import { isWalletPaymentMethod, isStripeElementPaymentMethod } from '../types';
import { WalletPaymentMethod } from './wallet/WalletPaymentMethod';
import { StripePaymentMethod } from './stripe/StripePaymentMethod';
import { WireTransferPaymentMethod } from './wireTransfer/WireTransferPaymentMethod';
import { ApplePayPaymentMethod } from './applePay/ApplePayPaymentMethod';
import { PayPalPaymentMethod } from './payPal/PayPalPaymentMethod';
import { KlarnaPaymentMethod } from './klarna/KlarnaPaymentMethod';
import { CreditCardPaymentMethod } from './creditCard/CreditCardPaymentMethod';
import { AchPaymentMethod } from './ach/AchPaymentMethod';

import { PaymentMethodSelectOptions_buyer$key } from './__generated__/PaymentMethodSelectOptions_buyer.graphql';
import { PaymentMethodSelectOptions_viewer$key } from './__generated__/PaymentMethodSelectOptions_viewer.graphql';

const viewerFragment = graphql`
    fragment PaymentMethodSelectOptions_viewer on Viewer {
        ...WalletPaymentMethod_viewer
        ...PayPalPaymentMethod_viewer
    }
`;

const buyerFragment = graphql`
    fragment PaymentMethodSelectOptions_buyer on User {
        ...WalletPaymentMethod_buyer
        ...PayPalPaymentMethod_buyer
    }
`;

type Props = {
    viewer: PaymentMethodSelectOptions_viewer$key;
    buyer: PaymentMethodSelectOptions_buyer$key | null;
};

export const PaymentMethodSelectOptions: FC<Props> = props => {
    const { viewer: viewerRef, buyer: buyerRef } = props;
    const { paymentMethods, hideWallet } = usePaymentMethodSelectContext();

    const viewer = useFragment(viewerFragment, viewerRef);
    const buyer = useFragment(buyerFragment, buyerRef);

    const showWallet = paymentMethods.some(isWalletPaymentMethod) && !hideWallet;
    const showStripe = paymentMethods.some(isStripeElementPaymentMethod);
    const customPaymentMethods = paymentMethods.filter(
        paymentMethod => !isStripeElementPaymentMethod(paymentMethod)
    );

    const isStandaloneForm = showStripe && paymentMethods.length === 1 && hideWallet;

    return (
        <>
            <ul>
                {showWallet && <WalletPaymentMethod buyer={buyer} viewer={viewer} />}
                {showStripe && <StripePaymentMethod />}
                {customPaymentMethods.map(({ paymentMethodType, paymentProcessor }) => {
                    switch (paymentMethodType) {
                        case 'CREDIT_CARD':
                            if (paymentProcessor === 'BRAINTREE') {
                                return (
                                    <CreditCardPaymentMethod
                                        key={`credit-card-${paymentProcessor}`}
                                    />
                                );
                            } else {
                                return null;
                            }
                        case 'ACH': {
                            if (paymentProcessor === 'BRAINTREE') {
                                return <AchPaymentMethod key={`ach-${paymentProcessor}`} />;
                            } else {
                                return null;
                            }
                        }
                        case 'PAYPAL_ACCOUNT':
                            if (paymentProcessor === 'BRAINTREE') {
                                return (
                                    <PayPalPaymentMethod
                                        key={`paypal-account-${paymentProcessor}`}
                                        viewer={viewer}
                                        buyer={buyer}
                                    />
                                );
                            } else {
                                return null;
                            }
                        case 'WIRE_TRANSFER':
                        case 'ON_PLATFORM_WIRE_TRANSFER':
                            return (
                                <WireTransferPaymentMethod
                                    key={`wire-transfer-${paymentProcessor}`}
                                    paymentMethodType={paymentMethodType}
                                    paymentProcessor={paymentProcessor}
                                />
                            );
                        case 'APPLE_PAY':
                            return (
                                <ApplePayPaymentMethod
                                    key={`apple-pay-${paymentProcessor}`}
                                    paymentProcessor={paymentProcessor}
                                />
                            );
                        case 'KLARNA':
                            return <KlarnaPaymentMethod key={`klarna-${paymentProcessor}`} />;
                        default:
                            // eslint-disable-next-line no-console
                            console.warn(
                                `PaymentMethodSelect: ${paymentMethodType} not supported yet.`
                            );
                            return null;
                    }
                })}
            </ul>
            {!isStandaloneForm && (
                <div
                    className={cn(
                        dibsCss.border,
                        dibsCss.borderDolphin,
                        dibsCss.borderB0,
                        dibsCss.borderSolid
                    )}
                />
            )}
        </>
    );
};
